import {useState, useEffect, useRef} from 'react'

import {useSelector, useDispatch} from 'react-redux'

import {isEmpty} from 'lodash'

import {UAParser} from 'ua-parser-js'

import {resetClickedMarker, setClickedMarkerData} from '../actions'

import {MarkerClusterer} from '@googlemaps/markerclusterer'

import {
  selectUserMarker,
  selectMarkerQueue,
  selectIsUserLocated,
  selectUserCoordinates,
  selectSearchTypeFilter,
  selectClickedMarkerData
} from '../selectors'


import {
  icons,
  createMapMarker,
  createVenueInfoWindow,
  createPerformanceMapInfoWindow
} from '../helpers'



const parser = new UAParser().getDevice()
const isMobile = parser.type === 'mobile'


// Create markers without calling marker.setMap(map)
async function createMarkers(map, items, clickCallback, setMap = false) {
  const markers = new Map()

  await Promise.all(
    items.map(async (item) => {
      const data = {...item, isSelected: false}
      const coordinates = {
        lat: item.attributes.coordinates[1],
        lng: item.attributes.coordinates[0],
      }

      const marker = await createMapMarker(map, coordinates, item.type, data, setMap)
      marker.addEventListener('gmp-click', () => clickCallback(item))
      markers.set(item.id, marker)
    })
  )

  return Object.fromEntries(markers)
}



function updateMarkerAppearance(marker, isSelected) {
  if (!marker) return

  const type = marker.data.type
  const scaleFactor = isSelected ? 1.4 : 1.0
  const isPremier = marker.data?.attributes?.isPremier
  
  if (isPremier) {
    marker.zIndex = window.google.maps.Marker.MAX_ZINDEX + 10
    marker.content.style.transform = `scale(${scaleFactor})`
    marker.data = {isSelected, ...marker.data}
    return
  }
  
  const newIconUrl = isSelected 
    ? icons['selected' + type.charAt(0).toUpperCase() + type.slice(1)] 
    : icons[type]

  const img = marker.content.querySelector('img')
  if (img) {
    img.src = newIconUrl
  }

  marker.zIndex = isSelected ? window.google.maps.Marker.MAX_ZINDEX + 10 : 5
  marker.content.style.transform = `scale(${scaleFactor})`
  marker.data = {isSelected, ...marker.data}
}




export function useCreateMapMarkers(map) {
  const dispatch = useDispatch()
  const infoWindow = useRef()
  const markerQueue = useSelector(selectMarkerQueue)
  const selectedMarker = useRef()

  const markerTypes = useSelector(selectSearchTypeFilter)

  // User
  const userMarker = useSelector(selectUserMarker)
  const isUserLocated = useSelector(selectIsUserLocated)
  const userCoordinates = useSelector(selectUserCoordinates)

  const clickedMarkerData = useSelector(selectClickedMarkerData)

  // Venues
  const venueMarkers = useRef({})

  // Performances
  const performanceMarkers = useRef({})

  // Hold cluster element
  const clustererRef = useRef(null)
  const [clusterCount, setClusterCount] = useState(0) // Used to trigger clustering


  // Load libraries if needed
  useEffect(() => {
    async function loadLibraries() {
      // eslint-disable-next-line
      const {Map, InfoWindow} = await window.google.maps.importLibrary('maps')
    }
    loadLibraries()


    return () => {
      // Cleanup
      if (clustererRef.current) {
        clustererRef.current.clearMarkers()
        clustererRef.current = null
      }

      // Reset the selected marker
      if (selectedMarker.current) {
        selectedMarker.current.setMap(null)
        selectedMarker.current = null
      }

      // Close info window
      if (infoWindow.current) {
        infoWindow.current.close()
        infoWindow.current = null
      }

      // Reset Redux states as needed
      dispatch(resetClickedMarker())
    }
  }, [dispatch])




  // ------------------------------------------------------------
  // ⭐ Event markers - Add new performance markers to the map 
  // and remove stale markers using the queue as the source of 
  // truth for what needs to be shown on the map
  // ------------------------------------------------------------
  useEffect(() => {
    if (!map) return

    async function processMarkers() {
      let shouldCluster = false
      let staleMarkers = []
      let itemsToCreate = []

      // Identify new markers to add
      for (const [id, item] of markerQueue.performances) {
        if (!performanceMarkers.current.hasOwnProperty(id)) {
          itemsToCreate.push(item)
        }
      }

      // Identify markers to remove
      for (const id of Object.keys(performanceMarkers.current)) {
        if (!markerQueue.performances.has(id)) {
          staleMarkers.push(id)
        }
      }


      if (staleMarkers.length > 0) {
        shouldCluster = true
        staleMarkers.forEach(id => {
          if (performanceMarkers.current[id]) {
            performanceMarkers.current[id].setMap(null)
            delete performanceMarkers.current[id]
          }
        })
      }


      // Create new markers
      if (itemsToCreate.length > 0) {
        shouldCluster = true
        const clickCallback = (item) => dispatch(setClickedMarkerData(item))
        const markers = await createMarkers(map, itemsToCreate, clickCallback)

        for (const [id, marker] of Object.entries(markers)) {
          performanceMarkers.current[id] = marker
        }
      }

      // Trigger clustering
      if (shouldCluster) {
        setClusterCount(prev => prev + 1)
      }
    }

    processMarkers()
  }, [map, dispatch, markerQueue.performances])




  // ------------------------------------------------------------
  // ⭐ Venue markers - Add new venue markers to the map 
  // and remove stale markers using the queue as the source of 
  // truth for what needs to be shown on the map
  // ------------------------------------------------------------
  useEffect(() => {
    if (!map) return

    async function processMarkers() {
      let shouldCluster = false
      let staleMarkers = []
      let itemsToCreate = []


      // Identify new markers to add
      for (const [id, item] of markerQueue.venues) {
        if (!venueMarkers.current.hasOwnProperty(id)) {
          itemsToCreate.push(item)
        }
      }

      // Identify markers to remove
      for (const id of Object.keys(venueMarkers.current)) {
        if (!markerQueue.venues.has(id)) {
          staleMarkers.push(id)
        }
      }


      if (staleMarkers.length > 0) {
        shouldCluster = true
        staleMarkers.forEach(id => {
          if (venueMarkers.current[id]) {
            venueMarkers.current[id].setMap(null)
            delete venueMarkers.current[id]
          }
        })
      }


      // Create new markers
      if (itemsToCreate.length > 0) {
        shouldCluster = true
        const clickCallback = (item) => dispatch(setClickedMarkerData(item))
        const markers = await createMarkers(map, itemsToCreate, clickCallback)

        for (const [id, marker] of Object.entries(markers)) {
          venueMarkers.current[id] = marker
        }
      }

      // Trigger clustering
      if (shouldCluster) {
        setClusterCount(prev => prev + 1)
      }
    }

    processMarkers()
  }, [map, dispatch, markerQueue.venues])




  // ------------------------------------------------------------
  // ⭐ Clustering - Create or update the MarkerClusterer instance 
  // when markers change. Adjust maxZoom to the desired zoom level 
  // where clusters break apart (e.g., 12 for a city view)
  // ------------------------------------------------------------
  useEffect(() => {
    if (!map) return

    const allMarkers = [
      ...Object.values(venueMarkers.current),
      ...Object.values(performanceMarkers.current)
    ].filter(marker => {
      // If marker.data.type is 'venue', only keep if markerTypes includes 'venues'
      // If marker.data.type is 'performance', only keep if markerTypes includes 'performances'
      const markerType = (marker.data.type === 'venue') ? 'venues' : 'performances'
      return markerTypes.includes(markerType)
    })

    // Separate premier markers from regular markers.
    const premierMarkers = allMarkers.filter(
      (marker) => marker.data?.attributes?.isPremier
    )
    const nonPremierMarkers = allMarkers.filter(
      (marker) => !marker.data?.attributes?.isPremier
    ).filter((marker) => marker !== selectedMarker.current)


    if (clustererRef.current) {
      // 1) Markers to remove = in cluster but not in `nonPremierMarkers`
      const toRemove = clustererRef.current.markers.filter(
        (marker) => !nonPremierMarkers.includes(marker)
      )

      // 2) Markers to add = in `nonPremierMarkers` but not currently in the cluster
      const toAdd = nonPremierMarkers.filter(
        (marker) => !clustererRef.current.markers.includes(marker)
      )


      clustererRef.current.removeMarkers(toRemove)
      clustererRef.current.addMarkers(toAdd)
    } else {   
      clustererRef.current = new MarkerClusterer({
        map, 
        markers: nonPremierMarkers,
        algorithmOptions: {maxZoom: isMobile ? 12 : 14},
        onClusterClick: (e, cluster, map) => {
          // const currentZoom = map.getZoom()
          const newZoom = Math.min(isMobile ? 13 : 15, map.maxZoom || 21)
          map.panTo(cluster.position)
          map.setZoom(newZoom)
        }
      })
    }

    // Ensure premier markers are always visible.
    premierMarkers.forEach(marker => marker.setMap(map))
    if (selectedMarker.current) {
      selectedMarker.current.setMap(map)
    }
  }, [map, clusterCount, markerTypes])




  // ------------------------------------------------------------
  // ⭐ User - Add the user marker
  // ------------------------------------------------------------
  useEffect(() => {
    if (map && isUserLocated && !userMarker) {
      (async () => {
        try {
          const {AdvancedMarkerElement} = await window.google.maps.importLibrary('marker')
          const {lat, lng} = userCoordinates
          const coordinates = new window.google.maps.LatLng(lat, lng)
          const markerDiv = document.createElement('div')
          
          markerDiv.style.width = '60px'
          markerDiv.style.height = '60px'
          markerDiv.innerHTML = icons['user']
          markerDiv.style.display = 'flex'
          markerDiv.style.alignItems = 'center'
          markerDiv.style.justifyContent = 'center'
          
          const marker = new AdvancedMarkerElement({
            map,
            content: markerDiv,
            position: coordinates
          })

          dispatch({type: 'SET_USER_MARKER', payload: marker})
        } catch (error) {
          console.error('Error creating user marker:', error)
        }
      })()
    }
  }, [map, dispatch, userMarker, isUserLocated, userCoordinates])




  // ------------------------------------------------------------
  // ⭐ Clicked marker - Info window and selected marker 
  // appearance logic
  // ------------------------------------------------------------
  useEffect(() => {
    if (isEmpty(clickedMarkerData)) return

    const {id, type} = clickedMarkerData

    const marker = (type === 'venue') 
      ? venueMarkers.current[id] 
      : performanceMarkers.current[id]


    if (!marker) return

    // If clicking the same marker, do nothing
    if (selectedMarker.current === marker) return

    // If switching markers, reset the previous one
    if (selectedMarker.current) {
      updateMarkerAppearance(selectedMarker.current, false)
      selectedMarker.current.setMap(null)
      clustererRef.current?.addMarker(selectedMarker.current)
    }

    // Close any existing infoWindow
    if (infoWindow.current) {
      infoWindow.current.close()
      infoWindow.current = null
    }

    // Update the appearance of the new selected marker
    updateMarkerAppearance(marker, true)

    // Create the new infoWindow
    infoWindow.current = (type === 'venue')
      ? createVenueInfoWindow(clickedMarkerData)
      : createPerformanceMapInfoWindow(clickedMarkerData)

    // Open the new info window and reset the redux state 
    clustererRef.current?.removeMarker(marker)
    marker.setMap(map)
    infoWindow.current.open({map, anchor: marker})
    dispatch(resetClickedMarker())

    // // Add a new listener to reset selectedMarker on close
    window.google.maps.event.addListener(infoWindow.current, 'closeclick', () => {
      updateMarkerAppearance(marker, false)
      selectedMarker.current = null
      infoWindow.current = null
      clustererRef.current?.removeMarker(marker)
      marker.setMap(map)
    })

    // Set the selected marker
    selectedMarker.current = marker
  }, [map, dispatch, clickedMarkerData])
}



